/**
 * Harmony Design System — Token constants for the Specpoint new workspace.
 *
 * These mirror the `--hx-*` and `--theme-*` CSS custom properties defined in
 * harmony.css / Specpoint Workspace.html. Use these instead of hardcoding hex
 * values in makeStyles so that design changes propagate from one place.
 *
 * Organisation:
 *   HX        — brand-level "hx" tokens (always Harmony blue regardless of product theme)
 *   HARMONY   — semantic theme tokens (PPM light, the workspace default)
 *   GRADIENT  — all gradient strings
 *   FONT      — font families, sizes, weights, line-heights
 *   RADIUS    — border-radius values
 *   SHADOW    — box-shadow values
 *   SPACING   — spacing scale (4 px base)
 *   TRANSITION — transition timing values
 *   CHIP      — status chip palette
 */

// ---------------------------------------------------------------------------
// HX — Primary (Harmony Blue)
// ---------------------------------------------------------------------------
export const HX = {
    // Primary ramp
    primary:      '#4C92D9',
    primaryHover: '#3D7BC4',
    primaryDeep:  '#2F6FB8',
    primarySoft:  '#DCE9F6',
    primaryTint:  '#EDF3FB',

    // Ink / neutrals
    ink:        '#2A3447',
    ink2:       '#4A5568',
    ink3:       '#6B7280',

    // Borders
    border:     '#C4D2E5',
    borderSoft: '#DDE5F0',

    // Surfaces
    railBg:   'linear-gradient(180deg,#E1EAF6 0%,#D1DFF1 100%)',
    panelBg:  '#F0F4FA',
    canvasBg: '#E8EDF5',

    // Semantic
    success:      '#17A871',
    warning:      '#FFB020',
    error:        '#D83148',
    danger:       '#C0392B',
    info:         '#3366FF',
    accentOrange: '#E2832E',
} as const

// ---------------------------------------------------------------------------
// GRADIENT — all gradient strings
// ---------------------------------------------------------------------------
export const GRADIENT = {
    /** Deep-blue header bar */
    header: 'linear-gradient(135deg,#1F4F8A 0%,#2C6BB5 50%,#3D7BC4 100%)',
    /** Active / selected state */
    active: 'linear-gradient(135deg,#2F6FB8 0%,#4C92D9 100%)',
    /** Accent / softer blue */
    accent: 'linear-gradient(135deg,#4C92D9 0%,#6FB1F2 100%)',
    /** CTA pill / primary button */
    pill:   'linear-gradient(135deg,#2F6FB8 0%,#4C92D9 100%)',
    /** Dela AI — purple → indigo (reserved for AI surfaces only) */
    dela:   'linear-gradient(119deg,#8A33C2 0%,#423FE2 100%)',
    /** Topbar accent stripe */
    topbarStripe: 'linear-gradient(90deg,#D6EBFF 0%,#6FB8E8 25%,#00C8E8 50%,#6FB8E8 75%,#D6EBFF 100%)',
} as const

// ---------------------------------------------------------------------------
// HARMONY — PPM light semantic theme tokens
// ---------------------------------------------------------------------------
export const HARMONY = {
    // Primary
    primary:       '#4C92D9',
    primaryHover:  '#3D7BC4',
    primaryLight:  'rgba(76,146,217,0.10)',

    // Backgrounds
    pageBg:      '#E2E4E9',
    cardBg:      '#F7F8FA',
    navBg:       '#FCFDFF',
    inputBg:     '#FFFFFF',
    surfaceBg:   '#FFFFFF',
    elevatedBg:  '#F7F8FA',
    hoverBg:     '#E5E7EB',

    // Text
    textPrimary:   '#373F4E',
    textSecondary: '#525969',
    textMuted:     '#6B7280',
    textInverse:   '#FFFFFF',

    // Borders
    borderColor: '#BFC6D4',
    borderLight: '#E2E8F0',

    // Links
    linkColor: '#005BB3',

    // Semantic
    success: '#17A871',
    warning: '#FFB020',
    error:   '#D83148',
    info:    '#3366FF',

    // Shell chrome
    shellFooterBg: '#212631',
} as const

// ---------------------------------------------------------------------------
// FONT — families, scale, weights, line-heights
// ---------------------------------------------------------------------------
export const FONT = {
    family: {
        /** UI controls, labels, body copy */
        sans:    "'Roboto', system-ui, sans-serif",
        /** Headings, titles, pill labels */
        display: "'Lexend', ui-sans-serif, system-ui, sans-serif",
        /** Harmony canonical body (Figtree variant) */
        figtree: "'Figtree', ui-sans-serif, system-ui, sans-serif",
        /** Tokens, spec IDs, data values */
        mono:    "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
    },

    /** px sizes — use with pxToRem() in makeStyles */
    size: {
        xs:   10,   // overline
        sm:   11,   // meta / caption
        base: 12,   // label
        md:   13,   // ui label
        lg:   14,   // body default
        xl:   15,   // body comfortable
        '2xl': 16,  // h3 / panel title (lower bound)
        '3xl': 18,  // h3 (upper bound)
        '4xl': 20,  // h2 small
        '5xl': 24,  // h2
        '6xl': 28,  // h2 section
        '7xl': 40,  // h1 / display
    },

    weight: {
        light:    300,
        normal:   400,
        medium:   500,
        semibold: 600,
        bold:     700,
        extrabold: 800,
    },

    leading: {
        none:    1,
        tight:   1.25,
        snug:    1.375,
        normal:  1.5,
        relaxed: 1.625,
    },
} as const

// ---------------------------------------------------------------------------
// RADIUS — border-radius values (px)
// ---------------------------------------------------------------------------
export const RADIUS = {
    xs:   3,
    sm:   4,
    md:   6,
    /** Default card / button */
    base: 8,
    lg:   10,
    xl:   12,
    '2xl': 14,
    '3xl': 16,
    '4xl': 24,
    full: 9999,
} as const

// ---------------------------------------------------------------------------
// SHADOW — box-shadow strings
// ---------------------------------------------------------------------------
export const SHADOW = {
    sm:       '0 1px 2px 0 rgb(15 23 42 / 0.05)',
    md:       '0 4px 6px -1px rgb(15 23 42 / 0.1), 0 2px 4px -2px rgb(15 23 42 / 0.1)',
    lg:       '0 10px 15px -3px rgb(15 23 42 / 0.1), 0 4px 6px -4px rgb(15 23 42 / 0.1)',
    xl:       '0 20px 25px -5px rgb(15 23 42 / 0.1), 0 8px 10px -6px rgb(15 23 42 / 0.1)',
    '2xl':    '0 30px 35px -8px rgb(15 23 42 / 0.15), 0 12px 15px -7px rgb(15 23 42 / 0.12)',
    dialog:   '0 24px 70px rgba(15,23,42,0.34)',
    dropdown: '0 4px 12px rgba(0,0,0,0.15)',
    card:     '0 1px 2px rgba(15,23,42,0.05)',
    cardHover:'0 6px 18px rgba(15,23,42,0.12)',
    pill:     '0 2px 8px rgba(47,111,184,0.34)',
    insetSm:  'inset 0 1px 4px 0 rgba(0,0,0,0.10)',
    insetMd:  'inset 0 1px 4px 0 rgba(0,0,0,0.20)',
} as const

// ---------------------------------------------------------------------------
// SPACING — 4 px base scale (px values, use with theme.spacing or pxToRem)
// ---------------------------------------------------------------------------
export const SPACING = {
    0:    0,
    0.5:  2,
    1:    4,
    1.5:  6,
    2:    8,
    2.5:  10,
    3:    12,
    3.5:  14,
    4:    16,
    5:    20,
    6:    24,
    7:    28,
    8:    32,
    10:   40,
    12:   48,
    14:   56,
    16:   64,
    20:   80,
    24:   96,
} as const

// ---------------------------------------------------------------------------
// TRANSITION
// ---------------------------------------------------------------------------
export const TRANSITION = {
    fast: '150ms ease',
    base: '200ms ease',
    slow: '300ms ease',
} as const

// ---------------------------------------------------------------------------
// CHIP — status chip palette (bg / fg pairs)
// ---------------------------------------------------------------------------
export const CHIP = {
    blue:    { bg: '#c7f2ff', fg: '#205a9e' },
    error:   { bg: '#ffecef', fg: '#c1253a' },
    warning: { bg: '#fff1be', fg: '#ad5400' },
    success: { bg: '#dbf9a8', fg: '#316d15' },
    info:    { bg: '#d0dfff', fg: '#454bd4' },
    orange:  { bg: '#ffe2c0', fg: '#6c1f07' },
    pink:    { bg: '#ffddf3', fg: '#b82890' },
} as const

// ---------------------------------------------------------------------------
// BOARD — task/kanban column accent colors (used in ProjectTasksDialog)
// ---------------------------------------------------------------------------
export const BOARD = {
    new:        '#4C92D9',  // blue
    inprogress: '#E2A23A',  // amber
    complete:   '#3FA86B',  // green
} as const
